import { useCallback, useMemo } from 'react';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import LocalPrintshopRoundedIcon from '@material-ui/icons/LocalPrintshopRounded';

import { ROUTES } from 'enumsV2';
import LocalMallOutlinedIcon from '@material-ui/icons/LocalMallOutlined';
import FindInPageOutlinedIcon from '@material-ui/icons/FindInPageOutlined';
// import LocalShippingOutlinedIcon from '@material-ui/icons/LocalShippingOutlined';
import {
  Home,
  Resources,
  Workspaces,
  CampaignIcon,
  Outbox,
  SalesIcon,
  OrdersIcon,
  QuotesIcon,
  List
} from 'Components/Shared/Icons';
import { CompanyModuleId } from '@setvi/shared/services/react-query/query/user/types';
import { Flags, TextColor } from '@setvi/shared/enums';
import { useDialog } from '@setvi/shared/providers';

import { useAppContext } from 'Providers/AppProvider/AppContext';

import { useThoughtspot } from 'pages/thoughtspot/useThoughtspot';
import { CustomerView } from 'pages/products/customer-view';
import {
  AIIcon,
  CalendarIcon,
  QuoteIconV2
} from '@setvi/shared/components/sicons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MenuItemProps } from '../..';

const CUSTOMER_VIEW_DIALOG_ID = 'customer-view';

export const useUserMenuItems = () => {
  const { liveboards } = useThoughtspot();
  const { companyData, user } = useAppContext();
  const flags = useFlags();
  const { openDialog, closeDialog } = useDialog();

  const openCustomerView = useCallback(() => {
    const handleClose = () => closeDialog(CUSTOMER_VIEW_DIALOG_ID);
    openDialog(<CustomerView onClose={handleClose} />, {
      id: CUSTOMER_VIEW_DIALOG_ID,
      disableEnforceFocus: true
    });
  }, [openDialog, closeDialog]);

  const telabioMenuItems: MenuItemProps[] = useMemo(
    () => [
      {
        title: 'Home',
        flag: Flags.HOME,
        route: ROUTES.HOME,
        icon: <Home />
      },
      {
        title: 'Revenue Management',
        flag: Flags.DIGITAL_SHOWROOM_TELA_BIO_ONE,
        baseRoute: ROUTES.REVENUE_MANAGEMENT,
        icon: <List />,
        list: [
          {
            title: 'Catalog',
            moduleId: CompanyModuleId.Products,
            route: ROUTES.PRODUCTS_LIST_VIEW,
            baseRoute: ROUTES.PRODUCTS,
            flag: Flags.PRODUCTS
          },
          {
            title: 'Consignment',
            route: ROUTES.CONSIGNMENT
          },
          {
            title: 'Customers',
            route: ROUTES.CUSTOMERS
          },
          {
            title: 'DP Form',
            route: ROUTES.DP_FORM
          },
          {
            title: 'Invoices',
            flag: Flags.INVOICES,
            route: ROUTES.INVOICES,
            baseRoute: ROUTES.INVOICES
          },
          {
            title: 'Inventory',
            route: ROUTES.INVENTORY
          },
          {
            title: 'Inventory Checks',
            route: ROUTES.INVENTORY_CHECKS
          }
        ]
      },
      ...(liveboards?.length > 0
        ? [
            {
              title: 'Reporting',
              baseRoute: ROUTES.THOUGHTSPOT_LIVEBOARD,
              icon: <AssessmentOutlinedIcon />,
              flag: Flags.THOUGHTSPOT_LIVEBOARDS,
              list: liveboards.map(liveboard => ({
                title: liveboard.metadata_name,
                route: ROUTES.THOUGHTSPOT_LIVEBOARD_DETAILS.replace(
                  ':liveboardId',
                  liveboard.metadata_id.toString()
                )
              }))
            }
          ]
        : []),
      {
        title: 'Resources',
        moduleId: CompanyModuleId.Resources,
        flag: Flags.RESOURCES,
        route: ROUTES.RESOURCES,
        baseRoute: ROUTES.RESOURCES,
        icon: <Resources />
      },
      {
        title: 'Workspaces',
        icon: <Workspaces />,
        flag: Flags.WORKSPACES,
        baseRoute: ROUTES.WORKSPACES,
        list: [
          {
            title: 'Contacts & Groups',
            flag: Flags.CONTACTS_GROUPS,
            route: ROUTES.WORKSPACES_CONTACTS_GROUPS
          },
          ...(companyData?.MyResourcesAllowed
            ? [
                {
                  title: 'My Files',
                  flag: Flags.MY_FILES,
                  route: ROUTES.WORKSPACES_FILES
                }
              ]
            : []),
          {
            title: 'My Presentations',
            moduleId: CompanyModuleId.Presentations,
            flag: Flags.MY_PRESENTATIONS,
            route: ROUTES.WORKSPACES_PRESENTATIONS
          },
          {
            title: 'Snippets',
            flag: Flags.SNIPPETS,
            route: ROUTES.WORKSPACES_SNIPPETS
          },
          {
            title: 'Digital Rooms',
            childrenFlags: [Flags.DIGITAL_SHOWROOM_TELA_BIO_ONE],
            route: ROUTES.WORKSPACES_DIGITAL_ROOMS
          }
        ]
      },
      {
        title: 'Campaigns',
        icon: <CampaignIcon />,
        baseRoute: ROUTES.CAMPAIGNS,
        list: [
          {
            title: 'Email Campaigns',
            flag: Flags.EMAIL_CAMPAIGNS,
            route: ROUTES.MY_CAMPAIGNS
          },
          {
            title: 'Company Campaigns',
            flag: Flags.COMPANY_CAMPAIGNS,
            route: ROUTES.COMPANY_CAMPAIGNS
          }
        ]
      },
      {
        title: 'Events',
        flag: Flags.TELA_BIO_EVENTS,
        route: ROUTES.EVENTS,
        baseRoute: ROUTES.EVENTS,
        icon: <CalendarIcon fill="inherit" />
      },
      {
        title: 'Outbox',
        flag: Flags.OUTBOX,
        route: ROUTES.OUTBOX,
        baseRoute: ROUTES.OUTBOX,
        icon: <Outbox />
      },
      // ! This is only for Demo needs
      {
        title: 'Local Printer',
        flag: Flags.LOCAL_PRINTER,
        route: 'http://office.fedex.com/',
        icon: <LocalPrintshopRoundedIcon />,
        external: true
      }
    ],
    [companyData?.MyResourcesAllowed, liveboards]
  );

  const defaultMenuItems: MenuItemProps[] = useMemo(
    () => [
      {
        title: 'Home',
        flag: Flags.HOME,
        route: ROUTES.HOME,
        icon: <Home />
      },
      {
        title: 'Orders',
        icon: <OrdersIcon />,
        flag: Flags.ORDERS_V2,
        route: ROUTES.ORDERS
      },
      {
        title: 'Quotes',
        icon: <QuoteIconV2 fontSize="small" />,
        flag: Flags.QUOTES_V2,
        route: ROUTES.QUOTES,
        list: [
          {
            title: 'Quotes',
            flag: Flags.QUOTES_V2,
            route: ROUTES.QUOTES
          },
          {
            title: 'Auto RFQ',
            flag: Flags.AUTO_RFQ,
            route: ROUTES.AUTO_RFQ
          }
        ]
      },
      {
        title: 'Intelligence',
        flag: Flags.INTELLIGENCE_V2,
        // moduleId: CompanyModuleId.Orders,
        route: ROUTES.INTELLIGENCE,
        baseRoute: ROUTES.INTELLIGENCE,
        icon: <AIIcon fill="#92929D" />
      },
      {
        title: 'Intelligence',
        flag: Flags.SINGER_ACCOUNT_INTELLIGENCE,
        route: ROUTES.SINGER_ACCOUNT_INTELLIGENCE,
        icon: <AIIcon fill="#92929D" />
      },
      {
        title: 'Sales',
        icon: <SalesIcon />,
        // ! This is not needed. Orders/Quotes and AutoRFQ are not dependent on each other
        // flag: Flag.ORDERS || Flag.AUTO_RFQ,
        // moduleId: CompanyModuleId.Orders,
        baseRoute: ROUTES.SALES,
        list: [
          {
            title: 'Orders',
            icon: <OrdersIcon />,
            flag: Flags.ORDERS,
            hidden: flags[Flags.ORDERS_V2],
            route: ROUTES.ORDERS
          },
          {
            title: 'Quotes',
            icon: <QuotesIcon />,
            flag: Flags.QUOTES,
            hidden: flags[Flags.QUOTES_V2],
            route: ROUTES.QUOTES
          },
          {
            title: 'RFQ',
            flag: Flags.AUTO_RFQ,
            // moduleId: CompanyModuleId.Orders,
            route: ROUTES.AUTO_RFQ,
            baseRoute: ROUTES.SALES,
            hidden: flags[Flags.QUOTES_V2],
            icon: <FindInPageOutlinedIcon htmlColor={TextColor.Grey} />
          },
          {
            title: 'Intelligence',
            flag: Flags.WHITESPACE,
            // moduleId: CompanyModuleId.Orders,
            route: ROUTES.INTELLIGENCE,
            baseRoute: ROUTES.INTELLIGENCE,
            hidden: flags[Flags.INTELLIGENCE_V2],
            icon: <AIIcon fill={TextColor.Grey} />
          }
        ]
      },
      {
        title: 'Invoices',
        flag: Flags.INVOICES,
        route: ROUTES.INVOICES,
        baseRoute: ROUTES.INVOICES,
        icon: <SalesIcon />
      },
      {
        title: 'Resources',
        moduleId: CompanyModuleId.Resources,
        flag: Flags.RESOURCES,
        route: ROUTES.RESOURCES,
        baseRoute: ROUTES.RESOURCES,
        icon: <Resources />
      },
      {
        title: 'Catalog',
        moduleId: CompanyModuleId.Products,
        baseRoute: ROUTES.PRODUCTS,
        flag: Flags.PRODUCTS,
        icon: <LocalMallOutlinedIcon />,
        ...(flags[Flags.PRODUCTS_CUSTOMER_VIEW]
          ? {
              list: [
                {
                  title: 'Rep View',
                  route: ROUTES.PRODUCTS_LIST_VIEW
                },
                {
                  title: 'Customer View',
                  onClick: openCustomerView
                }
              ]
            }
          : {
              route: ROUTES.PRODUCTS_LIST_VIEW
            })
      },
      {
        title: 'Workspaces',
        icon: <Workspaces />,
        flag: Flags.WORKSPACES,
        baseRoute: ROUTES.WORKSPACES,
        list: [
          {
            title: 'Contacts & Groups',
            flag: Flags.CONTACTS_GROUPS,
            route: ROUTES.WORKSPACES_CONTACTS_GROUPS
          },
          ...(companyData?.MyResourcesAllowed
            ? [
                {
                  title: 'My Files',
                  flag: Flags.MY_FILES,
                  route: ROUTES.WORKSPACES_FILES
                }
              ]
            : []),
          {
            title: 'My Presentations',
            moduleId: CompanyModuleId.Presentations,
            flag: Flags.MY_PRESENTATIONS,
            route: ROUTES.WORKSPACES_PRESENTATIONS
          },
          {
            title: 'Snippets',
            flag: Flags.SNIPPETS,
            route: ROUTES.WORKSPACES_SNIPPETS
          },
          {
            title: 'Digital Rooms',
            childrenFlags: [Flags.DIGITAL_SHOWROOM_TELA_BIO_ONE],
            route: ROUTES.WORKSPACES_DIGITAL_ROOMS
          }
        ]
      },
      // {
      //   title: 'Sales',
      //   icon: <SalesIcon />,
      //   baseRoute: ROUTES.SALES,
      //   list: [
      //     {
      //       title: 'Orders',
      //       flag: Flag.ORDERS,
      //       moduleId: CompanyModuleId.Orders,
      //       route: ROUTES.ORDERS,
      //       baseRoute: ROUTES.SALES,
      //       icon: <LocalShippingOutlinedIcon htmlColor={TextColor.Grey} />
      //     },
      //     {
      //       title: 'RFQ',
      //       flag: Flag.AUTO_RFQ,
      //       // moduleId: CompanyModuleId.Orders,
      //       route: ROUTES.AUTO_RFQ,
      //       baseRoute: ROUTES.SALES,
      //       icon: <FindInPageOutlinedIcon htmlColor={TextColor.Grey} />
      //     }
      //   ]
      // },
      {
        title: 'Campaigns',
        icon: <CampaignIcon />,
        baseRoute: ROUTES.CAMPAIGNS,
        list: [
          {
            title: 'Email Campaigns',
            flag: Flags.EMAIL_CAMPAIGNS,
            route: ROUTES.MY_CAMPAIGNS
          },
          {
            title: 'Company Campaigns',
            flag: Flags.COMPANY_CAMPAIGNS,
            route: ROUTES.COMPANY_CAMPAIGNS
          }
        ]
      },
      {
        title: 'Outbox',
        flag: Flags.OUTBOX,
        route: ROUTES.OUTBOX,
        baseRoute: ROUTES.OUTBOX,
        icon: <Outbox />
      },
      // ! This is only for Demo needs
      {
        title: 'Local Printer',
        flag: Flags.LOCAL_PRINTER,
        route: 'http://office.fedex.com/',
        icon: <LocalPrintshopRoundedIcon />,
        external: true
      },
      {
        title: 'Revenue Management',
        flag: Flags.DIGITAL_SHOWROOM_TELA_BIO_ONE,
        baseRoute: ROUTES.REVENUE_MANAGEMENT,
        icon: <List />,
        list: [
          {
            title: 'DP Form',
            route: ROUTES.DP_FORM
          },
          {
            title: 'Inventory',
            route: ROUTES.INVENTORY
          },
          {
            title: 'Customers',
            route: ROUTES.CUSTOMERS
          },
          {
            title: 'Consignment',
            route: ROUTES.CONSIGNMENT
          },
          {
            title: 'Inventory Checks',
            route: ROUTES.INVENTORY_CHECKS
          }
        ]
      },
      ...(liveboards?.length > 0
        ? [
            {
              title: 'Reporting',
              baseRoute: ROUTES.THOUGHTSPOT_LIVEBOARD,
              icon: <AssessmentOutlinedIcon />,
              flag: Flags.THOUGHTSPOT_LIVEBOARDS,
              list: liveboards.map(liveboard => ({
                title: liveboard.metadata_name,
                route: ROUTES.THOUGHTSPOT_LIVEBOARD_DETAILS.replace(
                  ':liveboardId',
                  liveboard.metadata_id.toString()
                )
              }))
            }
          ]
        : []),
      {
        title: 'Events',
        flag: Flags.TELA_BIO_EVENTS,
        route: ROUTES.EVENTS,
        baseRoute: ROUTES.EVENTS,
        icon: <CalendarIcon fill="inherit" />
      }
    ],
    [companyData?.MyResourcesAllowed, flags, liveboards, openCustomerView]
  );

  const userMenuItems = useMemo(() => {
    if (user?.CompanyID === 3401) {
      return telabioMenuItems;
    }

    return defaultMenuItems;
  }, [user?.CompanyID, telabioMenuItems, defaultMenuItems]);

  return { userMenuItems };
};
